import { apiRequest } from './core/client';
import { assertRealMailboxTarget } from '@/constants/mailbox';
import { EmailSchema, parseArrayWithSchema, type Email } from './schemas';

export type { Email };

// ============================================================================
// Sender Digest
// ============================================================================

export interface FetchSenderDigestPayload {
  accountId: string;
  folderId: string;
  sender: { contactId?: string | null; email?: string | null };
  limit?: number;
  offset?: number;
  includeBodies?: boolean;
  signal?: AbortSignal;
}

export async function fetchSenderDigest(
  payload: FetchSenderDigestPayload
): Promise<{ emails: Email[]; totalMessages: number }> {
  const response = await apiRequest<unknown>({
    path: '/senders/digest',
    method: 'POST',
    accountId: payload.accountId,
    body: {
      accountId: payload.accountId,
      folderId: payload.folderId,
      sender: payload.sender,
      limit: payload.limit,
      offset: payload.offset,
      includeBodies: payload.includeBodies ?? false,
    },
    signal: payload.signal,
  });

  const record = response as Record<string, unknown>;
  const emailsRaw = Array.isArray(record.emails) ? record.emails : [];
  const emails = parseArrayWithSchema(EmailSchema, emailsRaw, 'fetchSenderDigest') ;
  const totalMessages =
    typeof record.totalMessages === 'number'
      ? record.totalMessages
      : typeof record.total_messages === 'number'
        ? record.total_messages
        : emails.length;

  return { emails, totalMessages };
}

// ============================================================================
// Sender Subjects
// ============================================================================

export interface FetchSenderSubjectsPayload {
  accountId: string;
  folderId: string;
  sender: { contactId?: string | null; email?: string | null };
  limit?: number;
  signal?: AbortSignal;
}

export interface SenderSubject {
  id: string;
  subject: string;
  date: Date;
}

export async function fetchSenderSubjects(
  payload: FetchSenderSubjectsPayload
): Promise<{ subjects: SenderSubject[]; totalMessages: number }> {
  const response = await apiRequest<unknown>({
    path: '/senders/subjects',
    method: 'POST',
    accountId: payload.accountId,
    body: {
      accountId: payload.accountId,
      folderId: payload.folderId,
      sender: payload.sender,
      limit: payload.limit,
    },
    signal: payload.signal,
  });

  const record = response as Record<string, unknown>;
  const subjectsRaw = Array.isArray(record.subjects) ? record.subjects : [];
  const subjects: SenderSubject[] = subjectsRaw
    .map((item) => {
      const subjectRecord = item as Record<string, unknown>;
      const dateStr = typeof subjectRecord.date === 'string' ? subjectRecord.date : null;
      return {
        id: typeof subjectRecord.id === 'string' ? subjectRecord.id : '',
        subject: typeof subjectRecord.subject === 'string' ? subjectRecord.subject : '',
        date: dateStr ? new Date(dateStr) : new Date(),
      };
    })
    .filter((s) => s.id);

  const totalMessages =
    typeof record.totalMessages === 'number'
      ? record.totalMessages
      : typeof record.total_messages === 'number'
        ? record.total_messages
        : subjects.length;

  return { subjects, totalMessages };
}

// ============================================================================
// Sender Bulk Action
// ============================================================================

export interface SenderBulkActionPayload {
  accountId: string;
  folderId: string;
  sender: { contactId?: string | null; email?: string | null };
  action: 'archive' | 'delete' | 'mark_read' | 'mark_unread' | 'block' | 'approve';
  targetMailboxPath?: string | null;
  limit?: number;
}

/**
 * Per-account affected set returned by the server (backend P5.6): the COMPLETE
 * list of message ids the bulk action touched — including messages the client
 * never loaded — plus the real source/target mailboxes. This is what makes an
 * honest undo possible for sender-wide actions.
 */
export interface SenderBulkAffected {
  accountId: string;
  action: string;
  messageIds: string[];
  sourceMailboxId: string | null;
  targetMailboxId: string | null;
}

export interface SenderBulkActionResult {
  processed: number;
  totalMessages: number;
  affected: SenderBulkAffected[];
}

function parseAffected(raw: unknown): SenderBulkAffected[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item) => {
      const record = item as Record<string, unknown>;
      const accountId =
        typeof record.accountId === 'string'
          ? record.accountId
          : record.accountId != null
            ? String(record.accountId)
            : '';
      const messageIds = Array.isArray(record.messageIds)
        ? record.messageIds.map((id) => String(id)).filter(Boolean)
        : [];
      return {
        accountId,
        action: typeof record.action === 'string' ? record.action : '',
        messageIds,
        sourceMailboxId: record.sourceMailboxId != null ? String(record.sourceMailboxId) : null,
        targetMailboxId: record.targetMailboxId != null ? String(record.targetMailboxId) : null,
      };
    })
    .filter((entry) => entry.accountId && entry.messageIds.length > 0);
}

export async function performSenderBulkAction(
  payload: SenderBulkActionPayload
): Promise<SenderBulkActionResult> {
  // Mutating sender op: the frontend invariant is to resolve unified ids to
  // real (account, folder) pairs BEFORE dispatch (resolveSenderGroupContexts).
  // (The read-only digest/subjects endpoints accept unified ids — the server
  // resolves them since backend P5.9 — but mutations must be explicit.)
  assertRealMailboxTarget(payload.accountId, 'performSenderBulkAction', 'account');
  assertRealMailboxTarget(payload.folderId, 'performSenderBulkAction');
  const response = await apiRequest<unknown>({
    path: '/senders/bulk-action',
    method: 'POST',
    accountId: payload.accountId,
    body: {
      accountId: payload.accountId,
      folderId: payload.folderId,
      sender: payload.sender,
      action: payload.action,
      targetMailboxPath: payload.targetMailboxPath,
      limit: payload.limit,
    },
  });

  const record = response as Record<string, unknown>;
  return {
    processed: typeof record.processed === 'number' ? record.processed : 0,
    totalMessages:
      typeof record.totalMessages === 'number'
        ? record.totalMessages
        : typeof record.total_messages === 'number'
          ? record.total_messages
          : 0,
    affected: parseAffected(record.affected),
  };
}

// ============================================================================
// Label Digest
// ============================================================================

export interface FetchLabelDigestPayload {
  accountId: string;
  folderId: string;
  labelId: string;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}

export async function fetchLabelDigest(
  payload: FetchLabelDigestPayload
): Promise<{ emails: Email[]; totalMessages: number }> {
  const response = await apiRequest<unknown>({
    path: '/auto-labels/digest',
    method: 'POST',
    accountId: payload.accountId,
    body: {
      accountId: payload.accountId,
      folderId: payload.folderId,
      labelId: payload.labelId,
      limit: payload.limit,
      offset: payload.offset,
    },
    signal: payload.signal,
  });

  const record = response as Record<string, unknown>;
  const emailsRaw = Array.isArray(record.emails) ? record.emails : [];
  const emails = parseArrayWithSchema(EmailSchema, emailsRaw, 'fetchLabelDigest') ;
  const totalMessages =
    typeof record.totalMessages === 'number'
      ? record.totalMessages
      : typeof record.total_messages === 'number'
        ? record.total_messages
        : emails.length;

  return { emails, totalMessages };
}

// ============================================================================
// Label Counts
// ============================================================================

export interface FetchLabelCountsPayload {
  accountId: string;
  folderId: string;
  signal?: AbortSignal;
}

export interface LabelCountResult {
  labelCounts: Array<{ labelId: string; count: number }>;
  noLabelCount: number;
}

export async function fetchLabelCounts(
  payload: FetchLabelCountsPayload
): Promise<LabelCountResult> {
  const response = await apiRequest<unknown>({
    path: '/auto-labels/counts',
    method: 'POST',
    accountId: payload.accountId,
    body: {
      accountId: payload.accountId,
      folderId: payload.folderId,
    },
    signal: payload.signal,
  });

  const record = response as Record<string, unknown>;
  const labelCountsRaw = Array.isArray(record.label_counts) ? record.label_counts : [];
  const labelCounts = labelCountsRaw
    .map((item) => {
      const itemRecord = item as Record<string, unknown>;
      return {
        labelId:
          typeof itemRecord.labelId === 'string'
            ? itemRecord.labelId
            : typeof itemRecord.label_id === 'string'
              ? itemRecord.label_id
              : '',
        count: typeof itemRecord.count === 'number' ? itemRecord.count : 0,
      };
    })
    .filter((item) => item.labelId && item.count > 0);

  const noLabelCount =
    typeof record.noLabelCount === 'number'
      ? record.noLabelCount
      : typeof record.no_label_count === 'number'
        ? record.no_label_count
        : 0;

  return { labelCounts, noLabelCount };
}
